import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { cn } from "@/lib/utils.js";
import {
  cardStyles,
  segmentedTabIdle,
  segmentedTabSelected,
  segmentedTabTrack,
} from "@/lib/status-styles.js";
import { useAssets } from "@/contexts/AssetContext.jsx";
import {
  useMappedDowntimeClassifications,
  useUnmappedDowntimeClassifications,
} from "@/hooks/useDowntimeClassifications.js";
import NeedsMappingTable from "@/components/downtime-classification/needs-mapping-table.jsx";
import YourMappingsTable from "@/components/downtime-classification/your-mappings-table.jsx";

/** @typedef {'needs-mapping' | 'your-mappings'} ClassificationTabId */

const TABS = /** @type {const} */ ([
  { id: "needs-mapping", label: "Needs Mapping" },
  { id: "your-mappings", label: "Your Mappings" },
]);

/**
 * Unique, sorted vendor names from fleet assets (same values as Fleet Overview).
 * @param {{ vendor?: string|null }[]} assets
 * @returns {string[]}
 */
function getUniqueAssetVendors(assets) {
  if (!Array.isArray(assets)) return [];

  const vendors = assets
    .map((asset) => String(asset.vendor ?? "").trim())
    .filter(Boolean);

  return [...new Set(vendors)].sort((a, b) => a.localeCompare(b));
}

/**
 * Tabbed panels for Needs Mapping and Your Mappings downtime classification grids.
 * @returns {JSX.Element}
 */
export default function DowntimeClassificationPanels() {
  /** @type {[ClassificationTabId, Function]} */
  const [activeTab, setActiveTab] = useState(
    /** @type {ClassificationTabId} */ ("needs-mapping")
  );
  /** Per-tab vendor filters so each grid keeps its own selection across toggles. */
  const [needsMappingVendors, setNeedsMappingVendors] = useState(
    /** @type {string[]|null} */ (null)
  );
  const [yourMappingsVendors, setYourMappingsVendors] = useState(
    /** @type {string[]|null} */ (null)
  );
  const { assets } = useAssets();
  const vendors = useMemo(() => getUniqueAssetVendors(assets), [assets]);
  const {
    rows: unmappedRows,
    isLoading: isLoadingUnmapped,
    error: unmappedError,
    refetch: refetchUnmapped,
  } = useUnmappedDowntimeClassifications(vendors);
  const {
    rows: mappedRows,
    isLoading: isLoadingMapped,
    error: mappedError,
    refetch: refetchMapped,
  } = useMappedDowntimeClassifications();

  /**
   * Refreshes both classification lists after a successful save.
   * @returns {Promise<void>}
   */
  const handleSaveSuccess = async () => {
    await Promise.all([refetchUnmapped(), refetchMapped()]);
  };

  return (
    <div className='space-y-4'>
      <nav className={segmentedTabTrack}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type='button'
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-colors",
                isActive ? segmentedTabSelected : segmentedTabIdle
              )}
              data-active={isActive}>
              {tab.label}
            </button>
          );
        })}
      </nav>

      <Card className={cn(cardStyles.panel, "overflow-hidden")}>
        <CardContent className='p-0'>
          {activeTab === "needs-mapping" ? (
            <NeedsMappingTable
              rows={unmappedRows}
              isLoading={isLoadingUnmapped}
              error={unmappedError}
              selectedVendors={needsMappingVendors}
              onSelectedVendorsChange={setNeedsMappingVendors}
              onSaveSuccess={handleSaveSuccess}
            />
          ) : (
            <YourMappingsTable
              rows={mappedRows}
              isLoading={isLoadingMapped}
              error={mappedError}
              selectedVendors={yourMappingsVendors}
              onSelectedVendorsChange={setYourMappingsVendors}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
